import { useCallback, useEffect, useRef, useState } from 'react';

interface TurnstileProps {
  sitekey: string;
  onChange?: (token: string | null) => void;
  onExpired?: () => void;
  onError?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
}

declare global {
  interface Window {
    turnstile: {
      render: (element: HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | null;
    };
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: (error: string) => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
}

export default function Turnstile({ sitekey, onChange, onExpired, onError, theme = 'light', size = 'normal' }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const isRenderingRef = useRef(false);

  // Create stable callback refs to prevent infinite re-renders
  const onChangeRef = useRef(onChange);
  const onExpiredRef = useRef(onExpired);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onChangeRef.current = onChange;
    onExpiredRef.current = onExpired;
    onErrorRef.current = onError;
  }, [onChange, onExpired, onError]);

  const clearTurnstile = useCallback(() => {
    try {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      setCaptchaLoaded(false);
      setCaptchaError(false);
      isRenderingRef.current = false;
    } catch {
      isRenderingRef.current = false;
    }
  }, []);

  const renderTurnstile = useCallback(() => {
    // Prevent multiple simultaneous renders
    if (!window.turnstile || !containerRef.current || widgetIdRef.current !== null || isRenderingRef.current) {
      return;
    }

    isRenderingRef.current = true;

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey,
        callback: (token: string) => {
          onChangeRef.current?.(token);
          setCaptchaLoaded(true);
          setCaptchaError(false);
        },
        'error-callback': () => {
          setCaptchaError(true);
          setCaptchaLoaded(false);
          onChangeRef.current?.(null);
          onErrorRef.current?.();
        },
        'expired-callback': () => {
          onChangeRef.current?.(null);
          onExpiredRef.current?.();
          setCaptchaLoaded(false);
        },
        theme,
        size,
      });

      if (widgetIdRef.current) {
        setCaptchaLoaded(true);
        setCaptchaError(false);
      }
    } catch {
      setCaptchaError(true);
      setCaptchaLoaded(false);
    } finally {
      isRenderingRef.current = false;
    }
  }, [sitekey, theme, size]);

  const resetTurnstile = useCallback(() => {
    try {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
        onChangeRef.current?.(null);
        setCaptchaLoaded(false);
        setCaptchaError(false);
      }
    } catch {
      // Silent fail for reset errors
    }
  }, []);

  // Load Turnstile script
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    // Check if script already loaded
    if (window.turnstile) {
      setScriptLoaded(true);
      return undefined;
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector('script[src*="turnstile/v0/api.js"]');
    if (existingScript) {
      // Poll for window.turnstile in case the load event already fired before this listener was attached
      const pollInterval = window.setInterval(() => {
        if (window.turnstile) {
          setScriptLoaded(true);
          window.clearInterval(pollInterval);
        }
      }, 200);
      const handleScriptLoad = () => {
        setScriptLoaded(true);
        window.clearInterval(pollInterval);
        existingScript.removeEventListener('load', handleScriptLoad);
      };
      existingScript.addEventListener('load', handleScriptLoad);
      return () => {
        window.clearInterval(pollInterval);
        existingScript.removeEventListener('load', handleScriptLoad);
      };
    }

    // Load the script
    const turnstileScript = document.createElement('script');
    turnstileScript.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    turnstileScript.async = true;
    turnstileScript.defer = true;

    turnstileScript.onload = () => {
      setScriptLoaded(true);
    };

    turnstileScript.onerror = () => {
      setCaptchaError(true);
    };

    document.head.appendChild(turnstileScript);

    return () => {
      if (turnstileScript.parentNode) {
        turnstileScript.parentNode.removeChild(turnstileScript);
      }
    };
  }, []);

  // Render Turnstile when script is loaded
  useEffect(() => {
    if (scriptLoaded && !widgetIdRef.current && !isRenderingRef.current) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        renderTurnstile();
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    return undefined;
  }, [scriptLoaded, renderTurnstile]);

  // Watchdog: if neither loaded nor errored after 15s, surface an error
  useEffect(() => {
    if (captchaLoaded || captchaError) {
      return undefined;
    }
    const watchdogId = window.setTimeout(() => {
      setCaptchaError(true);
    }, 15000);
    return () => window.clearTimeout(watchdogId);
  }, [captchaLoaded, captchaError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTurnstile();
    };
  }, [clearTurnstile]);

  // Expose reset function globally for backward compatibility
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).turnstileReset = resetTurnstile;
    }
  }, [resetTurnstile]);

  return (
    <div className='flex w-full flex-col items-center justify-center'>
      <div ref={containerRef} className='mx-auto' />
      {captchaError && (
        <div className='mt-2 text-[11px] font-[var(--np-font-mono,monospace)] uppercase tracking-[0.12em] text-[#ff6b6b]'>
          <span>
            Captcha falhou.{' '}
            <button
              type='button'
              onClick={() => {
                clearTurnstile();
                setTimeout(renderTurnstile, 100);
              }}
              className='cursor-pointer border-0 bg-transparent px-1 text-[var(--pst-green)] underline underline-offset-2 hover:text-[var(--pst-green-hot)]'
            >
              Tentar novamente
            </button>
          </span>
        </div>
      )}
      {!captchaLoaded && !captchaError && (
        <div className='mt-2 flex items-center gap-2'>
          <div className='size-4 animate-spin rounded-full border-2 border-[rgba(156,255,46,0.18)] border-t-[var(--pst-green)]' />
          <span className='text-[11px] font-[var(--np-font-mono,monospace)] uppercase tracking-[0.12em] text-[var(--pst-muted)]'>Verificando segurança…</span>
        </div>
      )}
    </div>
  );
}
